import React from 'react';
import { Volume2, Sparkles, Activity, ShieldCheck } from 'lucide-react';
import { AudioEngine } from '../audio/AudioEngine';

interface AudioUnlockModalProps {
  isOpen: boolean;
  onUnlocked: () => void;
}

export const AudioUnlockModal: React.FC<AudioUnlockModalProps> = ({
  isOpen,
  onUnlocked
}) => {
  if (!isOpen) return null;

  const handleUnlock = async () => {
    const engine = AudioEngine.getInstance();
    const success = await engine.unlock();
    if (success) {
      engine.playReferenceTone(1.2);
    }
    onUnlocked();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md animate-fade-in">
      <div className="max-w-md w-full glass-panel p-6 lg:p-8 rounded-3xl border border-cyan-500/30 shadow-2xl shadow-cyan-500/20 text-center space-y-6">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-cyan-500 to-blue-600 flex items-center justify-center mx-auto shadow-lg shadow-cyan-500/30 text-slate-950">
          <Activity className="w-8 h-8 animate-pulse" />
        </div>

        <div className="space-y-2">
          <h2 className="text-2xl font-extrabold text-white tracking-tight">
            Enable Audio Engine
          </h2>
          <p className="text-sm text-slate-300 leading-relaxed">
            Experience the IPCC AR6 WGI Summary for Policymakers through real-time procedural Web Audio synthesis & interactive data visualization.
          </p>
        </div>

        <div className="bg-slate-900/90 rounded-2xl p-4 border border-slate-800 text-left space-y-2 text-xs text-slate-400">
          <div className="flex items-center gap-2 text-cyan-400 font-semibold">
            <Sparkles className="w-4 h-4" />
            <span>Acoustic Rulebook Highlights</span>
          </div>
          <ul className="space-y-1.5 list-disc list-inside text-slate-300">
            <li><strong className="text-white">Baseline Anchor:</strong> 0.0°C anomaly = 261.63 Hz (C4 tone)</li>
            <li><strong className="text-white">Thermal Scaling:</strong> Temperature rise translates directly to pitch elevation</li>
            <li><strong className="text-white">Procedural Sound:</strong> 100% client-side synthesized synthesizers</li>
          </ul>
        </div>

        <button
          onClick={handleUnlock}
          className="w-full py-3.5 px-6 rounded-2xl bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 text-slate-950 font-bold text-sm tracking-wide shadow-xl shadow-cyan-500/25 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 cursor-pointer"
        >
          <Volume2 className="w-5 h-5 fill-current" />
          <span>Initialize Audio Engine & Start</span>
        </button>

        <p className="text-[11px] text-slate-500 flex items-center justify-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Zero external audio downloads &bull; Optimized for Cloudflare Edge</span>
        </p>
      </div>
    </div>
  );
};
